import { ISessionState, SessionState } from "../session/sessionState";
import { ILockedRecord, LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { Base } from "ui/Scripts/source/framework/base";

interface IPushSubscription {
    userCode: string;
}

export class IndexedDbManager {
    private static sessionStore = "sessionState";
    private static sessionKey = "session";
    private static lockedRecordsKey = "lockedRecords";
    private static pushSubscriptionStore = "pushSubscription";

    private static isNullOrEmpty(test: string): boolean {
        return test === null || test === undefined || test === "";
    }

    private static isEmptyArray(test: string[]): boolean {
        return test === null || test === undefined || test.length < 1;
    }

    private static openDb(): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            try {
                const request = indexedDB.open(appConfig.clientDatabaseName, 2);
                request.onsuccess = () => {
                    return resolve(request.result);
                };
                request.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("Opening IndexedDb failed!"));
                };
                request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                    const db = <IDBDatabase>(<any>event).target.result;
                    if (event.oldVersion < 1) {
                        db.createObjectStore(IndexedDbManager.sessionStore);
                    }
                    if (event.oldVersion < 2) {
                        const pushSubscriptionStore = db.createObjectStore(IndexedDbManager.pushSubscriptionStore);
                        pushSubscriptionStore.createIndex("iUserCode", "userCode");
                    }
                    const tx = <IDBTransaction>(<any>event).target.transaction;
                    tx.oncomplete = () => {
                        return resolve(db);
                    };
                };
            } catch (e) {
                console.error(e, appConfig.clientDatabaseName, 2);
                return reject(e);
            }
        });
    }

    private static getAllKeysFromIndex(db: IDBDatabase, storeName: string, indexName: string, indexValue: string): Promise<string[]> {
        return new Promise<string[]>((resolve, reject) => {
            const result: string[] = [];
            try {
                const tx = db.transaction([storeName], "readonly");
                const store = tx.objectStore(storeName);
                const index = store.index(indexName);
                const keyRange = IDBKeyRange.only(indexValue);
                const request = index.openCursor(keyRange);
                request.onsuccess = e => {
                    const cursor = <IDBCursor>(<any>e).target.result;
                    if (cursor) {
                        // number | string | Date | BufferSource | IDBArrayKey
                        result.push(<string>cursor.primaryKey);
                        cursor.continue();
                    } else {
                        resolve(result);
                    }
                };
                request.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("IndexedDb openCursor failed!"));
                };
            } catch (e) {
                console.error(e, [storeName], "readonly");
                return reject(e);
            }
        });
    }

    private static get<T>(db: IDBDatabase, storeName: string, key: string): Promise<T> {
        if (this.isNullOrEmpty(key)) return new Promise<T>((resolve) => { resolve(null); });
        return new Promise<undefined>((resolve, reject) => {
            try {
                const tx = db.transaction(storeName, "readonly");
                tx.onerror = (event) => {
                    console.log("IndexedDb get transaction failed!", event);
                };
                const store = tx.objectStore(storeName);
                const request = store.get(key);
                request.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("IndexedDb get failed!"));
                };
                request.onsuccess = () => {
                    return resolve(request.result);
                };
            } catch (e) {
                console.error(e, storeName, "readonly");
                return reject(e);
            }
        });
    }

    private static put<T>(db: IDBDatabase, storeName: string, value: T, key: string): Promise<void> {
        if (this.isNullOrEmpty(key)) return new Promise<void>((resolve) => { resolve(); });
        return new Promise<void>((resolve, reject) => {
            try {
                const tx = db.transaction(storeName, "readwrite");
                tx.oncomplete = () => {
                    return resolve();
                };
                tx.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("IndexedDb put transaction failed!"));
                };
                const store = tx.objectStore(storeName);
                const request = store.put(value, key);
                request.onerror = (event) => {
                    console.log("IndexedDb put failed!", event);
                };
            } catch (e) {
                console.error(e, storeName, "readwrite");
                return reject(e);
            }
        });
    }

    private static remove(db: IDBDatabase, storeName: string, key: string): Promise<void> {
        if (this.isNullOrEmpty(key)) return new Promise<void>((resolve) => { resolve(); });
        return new Promise<void>((resolve, reject) => {
            try {
                const tx = db.transaction(storeName, "readwrite");
                tx.oncomplete = () => {
                    return resolve();
                };
                tx.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("IndexedDb remove transaction failed!"));
                };
                const store = tx.objectStore(storeName);
                const request = store.delete(key);
                request.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("IndexedDb remove failed!"));
                };
                request.onsuccess = () => {
                    return resolve(request.result);
                };
            } catch (e) {
                console.error(e, storeName, "readwrite");
                return reject(e);
            }
        });
    }

    static async storeSessionState(sessionState: ISessionState): Promise<void> {
        try {
            const db = await IndexedDbManager.openDb();
            await IndexedDbManager.put<ISessionState>(db, IndexedDbManager.sessionStore, sessionState, IndexedDbManager.sessionKey);
        } catch (e) {
            console.error("storeSessionState", e);
        }
    }

    static async getSessionState(): Promise<ISessionState> {
        try {
            const db = await IndexedDbManager.openDb();
            const sessionState = await IndexedDbManager.get<ISessionState>(db, IndexedDbManager.sessionStore, IndexedDbManager.sessionKey);
            return sessionState !== null && sessionState !== undefined ? new SessionState(sessionState) : null;
        } catch (e) {
            console.error("getSessionState", e);
            return null;
        }
    }

    static async storePushSubscription(pushSubscriptionId: string, userCode: string): Promise<void> {
        try {
            const db = await IndexedDbManager.openDb();
            await IndexedDbManager.put<IPushSubscription>(db, IndexedDbManager.pushSubscriptionStore, { userCode }, pushSubscriptionId);
        } catch (e) {
            console.error("storePushSubscription", e);
        }
    }

    static async getPushSubscriptionIds(userCode: string): Promise<string[]> {
        if (!userCode) return new Promise<string[]>((resolve) => { resolve([]); });
        try {
            const db = await IndexedDbManager.openDb();
            return await IndexedDbManager.getAllKeysFromIndex(db, IndexedDbManager.pushSubscriptionStore, "iUserCode", userCode);
        } catch (e) {
            console.error("getPushSubscriptionIds", e);
            return [];
        }
    }

    private static removePushSubscriptionsFromDbByIds(db: IDBDatabase, subscriptionIds: string[]): Promise<void> {
        if (this.isEmptyArray(subscriptionIds)) return new Promise<void>((resolve) => { resolve(); });
        return new Promise<void>((resolve, reject) => {
            try {
                const tx = db.transaction(IndexedDbManager.pushSubscriptionStore, "readwrite");
                tx.oncomplete = () => {
                    return resolve();
                };
                tx.onerror = (event) => {
                    console.log(event);
                    return reject(new Error("Removing subscriptions failed!"));
                };
                const store = tx.objectStore(IndexedDbManager.pushSubscriptionStore);
                for (let i = 0; i < subscriptionIds.length; i++) {
                    const subscriptionId = subscriptionIds[i];
                    const request = store.delete(IDBKeyRange.only(subscriptionId));
                    request.onerror = (event) => {
                        console.log("Removing subscription '" + subscriptionId + "' failed!", event);
                    };
                }
            } catch (e) {
                console.log(e);
                return reject(e);
            }
        });
    }

    static async removePushSubscriptions(subscriptionIds: string[]): Promise<void> {
        if (this.isEmptyArray(subscriptionIds)) return new Promise<void>((resolve) => { resolve(); });
        try {
            const db = await IndexedDbManager.openDb();
            await IndexedDbManager.removePushSubscriptionsFromDbByIds(db, subscriptionIds);
        } catch (e) {
            console.error("removePushSubscriptions", e);
        }
    }

    static async removeUserPushSubscriptions(userCode: string): Promise<string[]> {
        if (this.isNullOrEmpty(userCode)) return new Promise<string[]>((resolve) => { resolve([]); });
        try {
            const db = await IndexedDbManager.openDb();
            const keys = await IndexedDbManager.getAllKeysFromIndex(db, IndexedDbManager.pushSubscriptionStore, "iUserCode", userCode);
            await IndexedDbManager.removePushSubscriptionsFromDbByIds(db, keys);
            db.close();
            return new Promise<string[]>((resolve) => { resolve(keys); });
        } catch (e) {
            console.error("removePushSubscriptions", e);
        }
    }

    static async addLockedRecords(addLockedRecords: ILockedRecord[]): Promise<void> {
        if (!addLockedRecords || addLockedRecords.length < 1) return;
        try {
            const db = await IndexedDbManager.openDb();
            const dbLockedRecords = await IndexedDbManager.get<ILockedRecord[]>(db, IndexedDbManager.sessionStore, IndexedDbManager.lockedRecordsKey);
            const addIds = addLockedRecords.map(i => i.id);
            const lockedRecords = Base.getTypedArray(dbLockedRecords, LockedRecord).filter(i => addIds.indexOf(i.id) < 0).concat(addLockedRecords);
            await IndexedDbManager.put<ILockedRecord[]>(db, IndexedDbManager.sessionStore, lockedRecords, IndexedDbManager.lockedRecordsKey);
        } catch (e) {
            console.error("storeLockedRecord", e);
        }
    }

    static async removeLockedRecords(ids: string[]): Promise<void> {
        if (!ids || ids.length < 1) return;
        try {
            const db = await IndexedDbManager.openDb();
            const dbLockedRecords = await IndexedDbManager.get<ILockedRecord[]>(db, IndexedDbManager.sessionStore, IndexedDbManager.lockedRecordsKey);
            const lockedRecords = Base.getTypedArray(dbLockedRecords, LockedRecord).filter(i => ids.indexOf(i.id) < 0);
            await IndexedDbManager.put<ILockedRecord[]>(db, IndexedDbManager.sessionStore, lockedRecords, IndexedDbManager.lockedRecordsKey);
        } catch (e) {
            console.error("removeLockedRecords", e);
        }
    }

    static async getLockedRecords(): Promise<ILockedRecord[]> {
        try {
            const db = await IndexedDbManager.openDb();
            const dbLockedRecords = await IndexedDbManager.get<ILockedRecord[]>(db, IndexedDbManager.sessionStore, IndexedDbManager.lockedRecordsKey);
            return Base.getTypedArray(dbLockedRecords, LockedRecord);
        } catch (e) {
            console.error("getLockedRecords", e);
            return null;
        }
    }
}